import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import axios from "axios";
import { Navigate, Route, Routes, Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  LogOut, Loader2, RefreshCw, Plus, Trash2, MessageCircle, IndianRupee,
  Calendar as CalIcon, ChevronLeft, ChevronRight, ClipboardList, Ban, X,
  Link2, Copy, TicketPercent, Check, Pencil, Inbox, Mail, Archive, Package as PackageIcon, Star, Upload, Image as ImageIcon,
} from "lucide-react";
import { WHATSAPP_NUMBER } from "@/lib/lime-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "lime_admin_token";

// -------- token helpers --------
const getToken = () => localStorage.getItem(TOKEN_KEY);
const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
const clearToken = () => localStorage.removeItem(TOKEN_KEY);
const authHeader = () => ({ Authorization: `Bearer ${getToken()}` });

const STATUS_META = {
  paid:              { label: "Paid",             cls: "bg-[#E4FF00] text-black" },
  paid_manual:       { label: "Paid · Manual",    cls: "bg-[#E4FF00] text-black" },
  partial_payment:   { label: "Partial",          cls: "bg-yellow-500 text-black" },
  pending_payment:   { label: "Pending",          cls: "bg-orange-500 text-black" },
  created:           { label: "Held (unpaid)",    cls: "bg-[#333] text-[#A3A3A3]" },
  signature_failed:  { label: "Failed",           cls: "bg-red-600 text-white" },
};

const TIME_SLOTS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);

// -------- Login --------
function AdminLogin() {
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (getToken()) nav("/admin", { replace: true });
  }, [nav]);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      setToken(data.token);
      toast.success("Signed in");
      nav("/admin", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      setError(typeof msg === "string" ? msg : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f4f4f0] grain flex items-center justify-center p-4">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm border border-[#262626] bg-[#0f0f0f] p-8 space-y-5"
      >
        <div>
          <div className="font-mono-overline text-[#E4FF00]">Admin</div>
          <h1 className="font-display text-3xl font-black tracking-tighter mt-2">Sign in.</h1>
          <p className="text-xs text-[#A3A3A3] mt-2 leading-relaxed">
            LIME Studios internal bookings dashboard.
          </p>
        </div>
        <div>
          <Label className="font-mono-overline text-[#A3A3A3]">Username</Label>
          <Input
            data-testid="admin-username-input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none focus:border-[#E4FF00]"
          />
        </div>
        <div>
          <Label className="font-mono-overline text-[#A3A3A3]">Password</Label>
          <Input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none focus:border-[#E4FF00]"
          />
        </div>
        {error && (
          <div className="text-xs text-red-400 font-mono" data-testid="admin-login-error">{error}</div>
        )}
        <Button
          type="submit"
          data-testid="admin-login-button"
          disabled={loading || !username || !password}
          className="w-full bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Sign in
        </Button>
      </form>
    </div>
  );
}

// -------- Auth guard --------
function RequireAdmin({ children }) {
  const [state, setState] = useState("checking"); // checking | ok | fail
  const location = useLocation();
  useEffect(() => {
    const t = getToken();
    if (!t) { setState("fail"); return; }
    axios.get(`${API}/admin/me`, { headers: authHeader() })
      .then(() => setState("ok"))
      .catch(() => { clearToken(); setState("fail"); });
  }, [location.pathname]);
  if (state === "checking") {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="h-6 w-6 text-[#E4FF00] animate-spin" />
      </div>
    );
  }
  if (state === "fail") return <Navigate to="/admin/login" replace />;
  return children;
}

// -------- Shared helpers --------
const durationToHours = (d = "") => {
  const s = d.toLowerCase();
  if (s.includes("per hour")) return 1;
  const m = s.match(/\d+/);
  return m ? parseInt(m[0], 10) : 1;
};
const timeToMinutes = (hhmm) => { const [h, m] = hhmm.split(":").map(Number); return h * 60 + m; };
const minutesToTime = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

// Display formatters (backend stores yyyy-MM-dd + HH:mm; UI shows dd/MM/yy + h:mm AM/PM)
const formatDateDMY = (yyyyMmDd) => {
  if (!yyyyMmDd || typeof yyyyMmDd !== "string") return "";
  const [y, m, d] = yyyyMmDd.split("-");
  if (!y || !m || !d) return yyyyMmDd;
  return `${d}/${m}/${y.slice(-2)}`;
};
const formatTimeAmPm = (hhmm) => {
  if (!hhmm || typeof hhmm !== "string" || !hhmm.includes(":")) return hhmm || "";
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
};

const bookingWhatsAppMessage = (b) => {
  const lines = [
    "*Booking Confirmation — LIME Studios*",
    "",
    `*Name:* ${b.name}`,
    `*Phone:* ${b.phone}`,
    ...(b.email ? [`*Email:* ${b.email}`] : []),
    "",
    `*Package:* ${b.package_name}`,
    `*Duration:* ${b.option_duration}`,
    `*Date:* ${formatDateDMY(b.booking_date)}`,
    `*Start Time:* ${formatTimeAmPm(b.start_time)}`,
    "",
    `*Amount:* ₹${(b.amount || 0).toLocaleString("en-IN")}`,
    ...(b.status === "partial_payment"
      ? [`*Paid:* ₹${(b.amount_paid || 0).toLocaleString("en-IN")}`,
         `*Pending:* ₹${(b.amount_pending || 0).toLocaleString("en-IN")}`]
      : b.status === "pending_payment"
        ? [`*Payment:* PENDING — please pay before the session`]
        : [`*Payment:* Received`]),
    ...(b.payment_method ? [`*Method:* ${b.payment_method}`] : []),
    "",
    `*Booking ID:* ${b.id}`,
    "",
    "Thanks for booking with LIME Studios. See you soon!",
  ];
  return lines.join("\n");
};

const shareBookingWhatsApp = (b) => {
  // Prefer customer's phone if it looks like a valid Indian number
  const raw = (b.phone || "").replace(/\D/g, "");
  const num = raw.length === 10 ? `91${raw}` : (raw.length >= 10 ? raw : WHATSAPP_NUMBER);
  const url = `https://wa.me/${num}?text=${encodeURIComponent(bookingWhatsAppMessage(b))}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

// -------- Manual booking dialog --------
function ManualBookingDialog({ open, onOpenChange, packages, onCreated, initialDate, initialTime, initialBooking }) {
  const isEdit = !!initialBooking;
  const [packageId, setPackageId] = useState("");
  const [optionDuration, setOptionDuration] = useState("");
  const [hours, setHours] = useState(1);
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("paid"); // paid | pending | partial
  const [amountPaid, setAmountPaid] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [discount, setDiscount] = useState("");
  const [extras, setExtras] = useState([]); // [{label:'', amount:''}]
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initialBooking) {
      // Edit mode — hydrate from existing doc
      setPackageId(initialBooking.package_id || "");
      setOptionDuration(initialBooking.option_duration || "");
      setHours(initialBooking.hours || 1);
      setDate(initialBooking.booking_date || format(new Date(), "yyyy-MM-dd"));
      setStartTime(initialBooking.start_time || "10:00");
      setName(initialBooking.name || "");
      setPhone(initialBooking.phone || "");
      setEmail(initialBooking.email || "");
      setNotes(initialBooking.notes || "");
      const st = initialBooking.status;
      setPaymentStatus(
        st === "partial_payment" ? "partial"
        : st === "pending_payment" ? "pending"
        : "paid"
      );
      setAmountPaid(initialBooking.amount_paid ? String(initialBooking.amount_paid) : "");
      setPaymentMethod(initialBooking.payment_method || "cash");
      setDiscount(initialBooking.discount ? String(initialBooking.discount) : "");
      setExtras((initialBooking.extras || []).map((x) => ({ label: x.label, amount: String(x.amount) })));
    } else {
      setPackageId(""); setOptionDuration(""); setHours(1);
      setDate(initialDate || format(new Date(), "yyyy-MM-dd"));
      setStartTime(initialTime || "10:00");
      setName(""); setPhone(""); setEmail(""); setNotes("");
      setPaymentStatus("paid"); setAmountPaid(""); setPaymentMethod("cash");
      setDiscount("");
      setExtras([]);
    }
  }, [open, initialDate, initialTime, initialBooking]);

  const pkg = packages.find((p) => p.id === packageId);
  const opt = pkg?.options.find((o) => o.duration === optionDuration);
  const isPerHour = optionDuration?.toLowerCase().startsWith("per hour");
  const effHours = isPerHour ? Math.max(1, Number(hours) || 1) : 1;

  // Expand duration options into a full 1..N hour ladder (matches /pricing +
  // customer BookingDialog) so admin sees every hourly rate at a glance.
  const displayOptions = useMemo(() => {
    if (!pkg) return [];
    const perHour = pkg.options.find((o) => /per hour/i.test(o.duration));
    if (!perHour) return pkg.options.map((o) => ({ label: `${o.duration} · ₹${o.price.toLocaleString("en-IN")}`, key: o.duration, underlying: o.duration, hours: 1 }));

    const perHourRate = perHour.price;
    const fixed = pkg.options
      .filter((o) => o !== perHour)
      .map((o) => {
        const m = /(\d+)\s*hour/i.exec(o.duration);
        return m ? { hours: parseInt(m[1], 10), opt: o } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.hours - a.hours);

    const priceFor = (h) => {
      const exact = fixed.find((t) => t.hours === h);
      if (exact) return { price: exact.opt.price, underlying: exact.opt.duration, hours: 1 };
      let remaining = h, total = 0;
      for (const t of fixed) {
        while (remaining >= t.hours) { total += t.opt.price; remaining -= t.hours; }
      }
      total += remaining * perHourRate;
      total = Math.min(total, h * perHourRate);
      return { price: total, underlying: perHour.duration, hours: h };
    };

    const maxHours = Math.max(8, ...fixed.map((t) => t.hours));
    return Array.from({ length: maxHours }, (_, i) => {
      const h = i + 1;
      const r = priceFor(h);
      const durLabel = `${h} Hour${h > 1 ? "s" : ""}`;
      return { key: durLabel, label: `${durLabel} · ₹${r.price.toLocaleString("en-IN")}`, underlying: r.underlying, hours: r.hours };
    });
  }, [pkg]);

  // Which ladder row is currently active?
  const activeDurationKey = isPerHour
    ? `${effHours} Hour${effHours > 1 ? "s" : ""}`
    : optionDuration;

  const pickDuration = (key) => {
    const row = displayOptions.find((d) => d.key === key);
    if (!row) { setOptionDuration(""); setHours(1); return; }
    setOptionDuration(row.underlying);
    setHours(row.hours);
  };

  // Fixed-duration tiers (largest first) for combo pricing
  const fixedTiers = useMemo(() => {
    if (!pkg) return [];
    return pkg.options
      .filter((o) => !o.duration.toLowerCase().startsWith("per hour"))
      .map((o) => {
        const m = o.duration.match(/\d+/);
        return { hours: m ? parseInt(m[0], 10) : 0, price: o.price };
      })
      .filter((t) => t.hours > 0)
      .sort((a, b) => b.hours - a.hours);
  }, [pkg]);

  const baseTotal = useMemo(() => {
    if (!opt) return 0;
    if (!isPerHour) return opt.price;
    // Greedy combo
    let remaining = effHours;
    let out = 0;
    for (const tier of fixedTiers) {
      while (remaining >= tier.hours) { out += tier.price; remaining -= tier.hours; }
    }
    out += remaining * opt.price;
    return Math.min(out, effHours * opt.price);
  }, [opt, isPerHour, effHours, fixedTiers]);

  const discountNum = Math.max(0, Math.min(baseTotal, Number(discount) || 0));
  const cleanedExtras = extras
    .map((x) => ({ label: (x.label || "").trim(), amount: Math.max(0, Number(x.amount) || 0) }))
    .filter((x) => x.label && x.amount > 0);
  const extrasTotal = cleanedExtras.reduce((s, x) => s + x.amount, 0);
  const total = Math.max(0, baseTotal - discountNum + extrasTotal);

  const pending = paymentStatus === "paid" ? 0
    : paymentStatus === "pending" ? total
    : Math.max(0, total - Number(amountPaid || 0));

  const canSubmit = !!(pkg && opt && date && startTime && name && phone.length >= 10 &&
    discountNum < baseTotal &&
    (paymentStatus !== "partial" || (Number(amountPaid) > 0 && Number(amountPaid) < total)));

  const submit = async () => {
    setLoading(true);
    try {
      const body = {
        package_id: pkg.id,
        option_duration: opt.duration,
        amount: total,
        hours: effHours,
        discount: discountNum,
        extras: cleanedExtras,
        name, phone, email, notes,
        booking_date: date,
        start_time: startTime,
        payment_status: paymentStatus,
        amount_paid: paymentStatus === "partial" ? Number(amountPaid) : 0,
        payment_method: paymentMethod,
      };
      let data;
      if (isEdit) {
        const res = await axios.patch(`${API}/admin/bookings/${initialBooking.id}`, body, { headers: authHeader() });
        data = res.data;
        toast.success("Booking updated");
      } else {
        const res = await axios.post(`${API}/admin/bookings/manual`, body, { headers: authHeader() });
        data = res.data;
        toast.success("Manual booking saved");
      }
      onOpenChange(false);
      onCreated?.(data.booking);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      toast.error(status === 409 ? "Slot already booked" : (typeof detail === "string" ? detail : "Failed to save booking"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="admin-manual-booking-dialog"
        className="bg-[#0a0a0a] border-[#262626] text-[#f4f4f0] max-w-3xl rounded-none p-0 overflow-hidden flex flex-col max-h-[92dvh]"
      >
        <DialogHeader className="border-b border-[#262626] px-6 py-5">
          <DialogTitle className="font-display text-2xl font-black tracking-tighter">{isEdit ? "Edit booking" : "Manual booking"}</DialogTitle>
        </DialogHeader>
        <div className="px-6 py-5 space-y-5 overflow-y-auto">
          {/* Package + duration */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-mono-overline text-[#E4FF00]">Package</Label>
              <select
                data-testid="mb-package"
                value={packageId}
                onChange={(e) => { setPackageId(e.target.value); setOptionDuration(""); }}
                className="mt-2 w-full bg-[#0a0a0a] border border-[#262626] p-2 text-sm rounded-none focus:border-[#E4FF00] outline-none"
              >
                <option value="">— select —</option>
                {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="font-mono-overline text-[#E4FF00]">Duration</Label>
              <select
                data-testid="mb-duration"
                value={activeDurationKey || ""}
                onChange={(e) => pickDuration(e.target.value)}
                disabled={!pkg}
                className="mt-2 w-full bg-[#0a0a0a] border border-[#262626] p-2 text-sm rounded-none focus:border-[#E4FF00] outline-none disabled:opacity-50"
              >
                <option value="">— select —</option>
                {displayOptions.map((d) => (
                  <option key={d.key} value={d.key}>{d.label}</option>
                ))}
              </select>
              <p className="text-[10px] text-[#666] font-mono mt-1">Combo rates auto-picked. For sessions longer than {displayOptions.length} hr, pick Per Hour and use the +/− below.</p>
            </div>
          </div>

          {isPerHour && (
            <div className="border border-[#262626] p-3 bg-[#0f0f0f] flex items-center justify-between">
              <Label className="font-mono-overline text-[#E4FF00]">Hours</Label>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setHours((h) => Math.max(1, Number(h) - 1))}
                  disabled={effHours <= 1}
                  data-testid="mb-hours-decrement"
                  className="h-9 w-9 border border-[#262626] hover:border-[#E4FF00] disabled:opacity-30 flex items-center justify-center"
                >−</button>
                <input
                  type="number"
                  min="1"
                  max="12"
                  value={hours}
                  onChange={(e) => setHours(Math.max(1, Math.min(12, parseInt(e.target.value || "1", 10))))}
                  data-testid="mb-hours-input"
                  className="w-16 h-9 text-center bg-[#0a0a0a] border border-[#262626] font-display font-bold"
                />
                <button
                  type="button"
                  onClick={() => setHours((h) => Math.min(12, Number(h) + 1))}
                  disabled={effHours >= 12}
                  data-testid="mb-hours-increment"
                  className="h-9 w-9 border border-[#262626] hover:border-[#E4FF00] disabled:opacity-30 flex items-center justify-center"
                >+</button>
              </div>
            </div>
          )}

          {/* Date + time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-mono-overline text-[#E4FF00]">Date</Label>
              <Input
                type="date"
                data-testid="mb-date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none text-[#f4f4f0]"
              />
            </div>
            <div>
              <Label className="font-mono-overline text-[#E4FF00]">Start time</Label>
              <select
                data-testid="mb-time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="mt-2 w-full bg-[#0a0a0a] border border-[#262626] p-2 text-sm rounded-none focus:border-[#E4FF00] outline-none"
              >
                {TIME_SLOTS.map((t) => <option key={t} value={t}>{formatTimeAmPm(t)}</option>)}
              </select>
            </div>
          </div>

          {/* Customer */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-mono-overline text-[#E4FF00]">Customer name</Label>
              <Input data-testid="mb-name" value={name} onChange={(e) => setName(e.target.value)}
                     className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none" placeholder="Full name" />
            </div>
            <div>
              <Label className="font-mono-overline text-[#E4FF00]">Phone</Label>
              <Input data-testid="mb-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
                     className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none" placeholder="+91 9xxxxxxxxx" />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-mono-overline text-[#E4FF00]">Email (optional)</Label>
              <Input data-testid="mb-email" value={email} onChange={(e) => setEmail(e.target.value)}
                     className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none" placeholder="customer@email.com" />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-mono-overline text-[#E4FF00]">Notes</Label>
              <Textarea data-testid="mb-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                        className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none" rows={2} />
            </div>
          </div>

          {/* Payment */}
          <div className="border border-[#262626] p-4 space-y-4 bg-[#0f0f0f]">
            <Label className="font-mono-overline text-[#E4FF00]">Payment</Label>
            <div className="flex flex-wrap gap-2">
              {[
                { id: "paid", label: "Paid" },
                { id: "pending", label: "Full Payment Pending" },
                { id: "partial", label: "Partially Paid" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  data-testid={`mb-pstatus-${opt.id}`}
                  onClick={() => setPaymentStatus(opt.id)}
                  className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${
                    paymentStatus === opt.id ? "bg-[#E4FF00] text-black border-[#E4FF00]" : "border-[#262626] text-[#A3A3A3]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {paymentStatus === "partial" && (
              <div>
                <Label className="font-mono-overline text-[#A3A3A3]">Amount paid (₹)</Label>
                <Input
                  data-testid="mb-amount-paid"
                  type="number"
                  min="1"
                  max={total > 0 ? total - 1 : undefined}
                  value={amountPaid}
                  onChange={(e) => setAmountPaid(e.target.value)}
                  className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none"
                  placeholder={total ? `1 – ${total - 1}` : ""}
                />
              </div>
            )}

            <div>
              <Label className="font-mono-overline text-[#A3A3A3]">Payment method</Label>
              <select
                data-testid="mb-pmethod"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="mt-2 w-full bg-[#0a0a0a] border border-[#262626] p-2 text-sm rounded-none"
              >
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="bank">Bank transfer</option>
                <option value="card">Card</option>
                <option value="razorpay_link">Razorpay Payment Link</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <Label className="font-mono-overline text-[#A3A3A3]">Discount (₹)</Label>
              <Input
                data-testid="mb-discount"
                type="number"
                min="0"
                max={baseTotal > 0 ? baseTotal - 1 : undefined}
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none"
                placeholder="0"
              />
              <p className="text-[10px] text-[#666] font-mono mt-1">Subtracted from the base price. Leave 0 for no discount.</p>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="font-mono-overline text-[#A3A3A3]">Additional charges (extra equipment, etc.)</Label>
                <Button
                  type="button"
                  onClick={() => setExtras((arr) => [...arr, { label: "", amount: "" }])}
                  data-testid="mb-extra-add"
                  variant="ghost"
                  className="text-[#E4FF00] hover:bg-transparent hover:text-[#CDE600] h-auto py-1 text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add line
                </Button>
              </div>
              {extras.length === 0 && (
                <p className="text-[10px] text-[#666] font-mono mt-2">No extras. Click &quot;Add line&quot; to bill for a boom mic, reflector, extra crew, etc.</p>
              )}
              <div className="mt-2 space-y-2">
                {extras.map((row, i) => (
                  <div key={i} className="flex gap-2 items-center" data-testid={`mb-extra-row-${i}`}>
                    <Input
                      data-testid={`mb-extra-label-${i}`}
                      value={row.label}
                      onChange={(e) => setExtras((arr) => arr.map((r, idx) => idx === i ? { ...r, label: e.target.value } : r))}
                      placeholder="e.g. Boom mic"
                      className="flex-1 bg-[#0a0a0a] border-[#262626] rounded-none"
                    />
                    <Input
                      data-testid={`mb-extra-amount-${i}`}
                      type="number"
                      min="0"
                      value={row.amount}
                      onChange={(e) => setExtras((arr) => arr.map((r, idx) => idx === i ? { ...r, amount: e.target.value } : r))}
                      placeholder="₹"
                      className="w-28 bg-[#0a0a0a] border-[#262626] rounded-none"
                    />
                    <button
                      type="button"
                      onClick={() => setExtras((arr) => arr.filter((_, idx) => idx !== i))}
                      data-testid={`mb-extra-remove-${i}`}
                      className="p-2 text-red-400 hover:bg-red-500/10"
                      title="Remove"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-4 pt-2 border-t border-[#262626]">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#A3A3A3]">Base</div>
                <div className="font-display text-lg font-bold flex items-center"><IndianRupee className="h-4 w-4" />{baseTotal.toLocaleString("en-IN")}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#A3A3A3]">Discount</div>
                <div className="font-display text-lg font-bold text-[#E4FF00] flex items-center">−<IndianRupee className="h-4 w-4" />{discountNum.toLocaleString("en-IN")}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#A3A3A3]">Extras</div>
                <div className="font-display text-lg font-bold text-orange-300 flex items-center">+<IndianRupee className="h-4 w-4" />{extrasTotal.toLocaleString("en-IN")}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#A3A3A3]">Total</div>
                <div className="font-display text-2xl font-black flex items-center"><IndianRupee className="h-5 w-5" />{total.toLocaleString("en-IN")}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#A3A3A3]">To collect</div>
                <div className="font-display text-2xl font-black text-orange-400 flex items-center"><IndianRupee className="h-5 w-5" />{pending.toLocaleString("en-IN")}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-[#262626] px-6 py-4 flex justify-end gap-2 shrink-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A3A3A3] hover:text-[#f4f4f0] hover:bg-transparent">Cancel</Button>
          <Button
            data-testid="mb-submit"
            onClick={submit}
            disabled={!canSubmit || loading}
            className="bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold px-6"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Save booking
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Block time dialog --------
function BlockDialog({ open, onOpenChange, onCreated, initialDate }) {
  const [date, setDate] = useState("");
  const [start, setStart] = useState("00:00");
  const [end, setEnd] = useState("23:59");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setDate(initialDate || format(new Date(), "yyyy-MM-dd"));
      setStart("10:00"); setEnd("18:00"); setReason("");
    }
  }, [open, initialDate]);

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/blocks`, {
        booking_date: date, start_time: start, end_time: end, reason,
      }, { headers: authHeader() });
      toast.success("Time blocked");
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to block");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-[#262626] text-[#f4f4f0] max-w-md rounded-none p-0 overflow-hidden">
        <DialogHeader className="border-b border-[#262626] px-5 py-4">
          <DialogTitle className="font-display text-xl font-black tracking-tighter">Block time</DialogTitle>
        </DialogHeader>
        <div className="p-5 space-y-4">
          <div>
            <Label className="font-mono-overline text-[#E4FF00]">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none" data-testid="bl-date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="font-mono-overline text-[#E4FF00]">From</Label>
              <Input type="time" value={start} onChange={(e) => setStart(e.target.value)}
                     className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none" data-testid="bl-start" />
            </div>
            <div>
              <Label className="font-mono-overline text-[#E4FF00]">To</Label>
              <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)}
                     className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none" data-testid="bl-end" />
            </div>
          </div>
          <div>
            <Label className="font-mono-overline text-[#E4FF00]">Reason (optional)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
                   placeholder="Studio maintenance, private hold…"
                   className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none" data-testid="bl-reason" />
          </div>
        </div>
        <div className="border-t border-[#262626] px-5 py-3 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-[#A3A3A3] hover:text-[#f4f4f0] hover:bg-transparent">Cancel</Button>
          <Button
            onClick={submit}
            disabled={loading}
            data-testid="bl-submit"
            className="bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Block time
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Day detail modal --------
function DayDetailDialog({ open, onOpenChange, date, bookings, blocks, onManualBook, onBlock, onRefresh }) {
  const dstr = date ? format(date, "yyyy-MM-dd") : "";
  const dayBookings = bookings.filter((b) => b.booking_date === dstr);
  const dayBlocks = blocks.filter((b) => b.booking_date === dstr);

  const timeline = useMemo(() => {
    // Build 24 rows, mark each with a booking/block if any overlaps
    const rows = TIME_SLOTS.map((t) => ({ t, bookings: [], blocks: [] }));
    for (const b of dayBookings) {
      const s = timeToMinutes(b.start_time);
      const parsed = durationToHours(b.option_duration);
      const h = Math.max(Number(b.hours) || 0, parsed) || 1;
      const len = h * 60 + 60; // include 1-hour wrap row as PAID
      for (let i = 0; i < 24; i++) {
        const rowStart = i * 60;
        if (rowStart < s + len && rowStart + 60 > s) rows[i].bookings.push(b);
      }
    }
    for (const bl of dayBlocks) {
      const s = timeToMinutes(bl.start_time);
      const e = timeToMinutes(bl.end_time);
      for (let i = 0; i < 24; i++) {
        const rowStart = i * 60;
        if (rowStart < e && rowStart + 60 > s) rows[i].blocks.push(bl);
      }
    }
    return rows;
  }, [dayBookings, dayBlocks]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-[#262626] text-[#f4f4f0] max-w-2xl rounded-none p-0 overflow-hidden flex flex-col max-h-[92dvh]">
        <div className="border-b border-[#262626] px-5 py-4 flex items-center justify-between">
          <div>
            <div className="font-mono-overline text-[#E4FF00]">Timeline</div>
            <h3 className="font-display text-xl font-black tracking-tighter mt-1">
              {date ? format(date, "EEE, dd/MM/yy") : ""}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => onManualBook(dstr)} data-testid="dd-add-booking"
                    className="bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold text-xs">
              <Plus className="h-4 w-4 mr-1" /> Booking
            </Button>
            <Button onClick={() => onBlock(dstr)} variant="ghost" data-testid="dd-block"
                    className="border border-[#262626] text-[#A3A3A3] hover:text-[#f4f4f0] rounded-none text-xs">
              <Ban className="h-4 w-4 mr-1" /> Block
            </Button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          {timeline.map((row) => {
            const busy = row.bookings.length > 0 || row.blocks.length > 0;
            return (
              <div key={row.t} className={`flex border-b border-[#1a1a1a] ${busy ? "bg-[#141414]" : ""}`}>
                <div className="w-24 shrink-0 py-3 px-4 text-xs font-mono text-[#A3A3A3] border-r border-[#1a1a1a]">
                  {formatTimeAmPm(row.t)}
                </div>
                <div className="flex-1 py-2 px-3 space-y-1">
                  {row.bookings.map((b) => (
                    <div key={b.id + row.t} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest ${STATUS_META[b.status]?.cls || "bg-[#262626] text-[#A3A3A3]"}`}>
                          {STATUS_META[b.status]?.label || b.status}
                        </span>
                        <span className="truncate">{b.name} · {b.package_name} · {b.option_duration}</span>
                      </div>
                      <button onClick={() => shareBookingWhatsApp(b)} className="text-[#25D366] hover:opacity-80 shrink-0" title="Share on WhatsApp">
                        <MessageCircle className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {row.blocks.map((bl) => (
                    <div key={bl.id + row.t} className="flex items-center justify-between text-xs text-[#A3A3A3]">
                      <span className="inline-flex items-center gap-2">
                        <Ban className="h-3 w-3 text-red-400" /> Blocked · {formatTimeAmPm(bl.start_time)}–{formatTimeAmPm(bl.end_time)} {bl.reason ? `· ${bl.reason}` : ""}
                      </span>
                      <button
                        onClick={async () => {
                          await axios.delete(`${API}/admin/blocks/${bl.id}`, { headers: authHeader() });
                          toast.success("Block removed");
                          onRefresh?.();
                        }}
                        className="text-red-400 hover:opacity-80"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  {!busy && <div className="text-xs text-[#555] font-mono">Free</div>}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Dashboard --------
function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("bookings"); // bookings | calendar
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [summary, setSummary] = useState({ total_bookings: 0, upcoming_bookings: 0, revenue_collected: 0 });
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [prefillDate, setPrefillDate] = useState("");
  const [prefillTime, setPrefillTime] = useState("");
  const [editBooking, setEditBooking] = useState(null);
  const [detailDate, setDetailDate] = useState(null);
  const [calMonth, setCalMonth] = useState(new Date());
  const [briefsUnread, setBriefsUnread] = useState(0);

  // Background poll for unread briefs count so the tab badge is always live
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/contacts?unread_only=true`, { headers: authHeader() });
        if (alive) setBriefsUnread(data?.unread_count || 0);
      } catch (e) { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, blRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers: authHeader() }),
        axios.get(`${API}/admin/blocks`, { headers: authHeader() }),
        axios.get(`${API}/admin/bookings/summary`, { headers: authHeader() }),
      ]);
      setBookings(bRes.data.items || []);
      setBlocks(blRes.data.items || []);
      setSummary(sRes.data || {});
    } catch (err) {
      if (err?.response?.status === 401) { clearToken(); nav("/admin/login", { replace: true }); }
      else toast.error("Failed to load data");
    } finally { setLoading(false); }
  }, [nav]);

  const loadPackages = useCallback(() => {
    axios.get(`${API}/packages`).then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadPackages();
    refresh();
  }, [refresh, loadPackages]);

  const logout = () => { clearToken(); nav("/admin/login", { replace: true }); };

  const filteredBookings = useMemo(() => {
    return bookings.filter((b) => {
      if (statusFilter !== "all" && b.status !== statusFilter) return false;
      if (dateFilter && b.booking_date !== dateFilter) return false;
      return true;
    });
  }, [bookings, statusFilter, dateFilter]);

  const openManual = (d, t) => { setEditBooking(null); setPrefillDate(d || ""); setPrefillTime(t || ""); setManualOpen(true); };
  const openEdit = (b) => { setEditBooking(b); setManualOpen(true); };
  const openBlock = (d) => { setPrefillDate(d || ""); setBlockOpen(true); };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f4f4f0]">
      {/* Header */}
      <header className="border-b border-[#262626] sticky top-0 z-30 bg-[#0a0a0a]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs font-mono uppercase tracking-widest text-[#A3A3A3] hover:text-[#f4f4f0]">← Site</Link>
            <div className="font-display text-xl font-black tracking-tighter">LIME · Admin</div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={refresh} variant="ghost" size="sm" data-testid="admin-refresh"
                    className="text-[#A3A3A3] hover:text-[#f4f4f0] hover:bg-transparent">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button onClick={() => openManual()} data-testid="admin-new-booking-btn"
                    className="bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold text-xs">
              <Plus className="h-4 w-4 mr-1" /> Manual booking
            </Button>
            <Button onClick={() => openBlock()} variant="ghost" data-testid="admin-block-btn"
                    className="border border-[#262626] text-[#A3A3A3] hover:text-[#f4f4f0] rounded-none text-xs">
              <Ban className="h-4 w-4 mr-1" /> Block time
            </Button>
            <Button onClick={logout} variant="ghost" size="sm" data-testid="admin-logout"
                    className="text-[#A3A3A3] hover:text-[#f4f4f0] hover:bg-transparent">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { label: "Total bookings", val: summary.total_bookings, testid: "stat-total" },
            { label: "Upcoming", val: summary.upcoming_bookings, testid: "stat-upcoming" },
            { label: "Revenue (₹)", val: (summary.revenue_collected || 0).toLocaleString("en-IN"), testid: "stat-revenue" },
            { label: "Blocked windows", val: blocks.length, testid: "stat-blocks" },
          ].map((s) => (
            <div key={s.label} className="border border-[#262626] p-4" data-testid={s.testid}>
              <div className="font-mono-overline text-[#A3A3A3]">{s.label}</div>
              <div className="font-display text-3xl font-black mt-1">{s.val}</div>
            </div>
          ))}
        </div>

        {/* Tab switch */}
        <div className="flex flex-wrap items-center gap-2 border-b border-[#262626] pb-3">
          <button
            onClick={() => setTab("bookings")}
            data-testid="tab-bookings"
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${
              tab === "bookings" ? "bg-[#E4FF00] text-black border-[#E4FF00]" : "border-[#262626] text-[#A3A3A3]"
            }`}
          >
            <ClipboardList className="inline h-3 w-3 mr-1" /> Bookings
          </button>
          <button
            onClick={() => setTab("calendar")}
            data-testid="tab-calendar"
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${
              tab === "calendar" ? "bg-[#E4FF00] text-black border-[#E4FF00]" : "border-[#262626] text-[#A3A3A3]"
            }`}
          >
            <CalIcon className="inline h-3 w-3 mr-1" /> Calendar
          </button>
          <button
            onClick={() => setTab("share")}
            data-testid="tab-share"
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${
              tab === "share" ? "bg-[#E4FF00] text-black border-[#E4FF00]" : "border-[#262626] text-[#A3A3A3]"
            }`}
          >
            <Link2 className="inline h-3 w-3 mr-1" /> Share links
          </button>
          <button
            onClick={() => setTab("coupons")}
            data-testid="tab-coupons"
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${
              tab === "coupons" ? "bg-[#E4FF00] text-black border-[#E4FF00]" : "border-[#262626] text-[#A3A3A3]"
            }`}
          >
            <TicketPercent className="inline h-3 w-3 mr-1" /> Coupons
          </button>
          <button
            onClick={() => setTab("briefs")}
            data-testid="tab-briefs"
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border relative ${
              tab === "briefs" ? "bg-[#E4FF00] text-black border-[#E4FF00]" : "border-[#262626] text-[#A3A3A3]"
            }`}
          >
            <Inbox className="inline h-3 w-3 mr-1" /> Briefs
            {briefsUnread > 0 && (
              <span
                data-testid="briefs-unread-badge"
                className={`absolute -top-2 -right-2 min-w-[20px] h-5 px-1 flex items-center justify-center text-[10px] font-mono font-black rounded-full ${
                  tab === "briefs" ? "bg-black text-[#E4FF00]" : "bg-[#E4FF00] text-black"
                }`}
              >
                {briefsUnread}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("packages")}
            data-testid="tab-packages"
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${
              tab === "packages" ? "bg-[#E4FF00] text-black border-[#E4FF00]" : "border-[#262626] text-[#A3A3A3]"
            }`}
          >
            <PackageIcon className="inline h-3 w-3 mr-1" /> Packages
          </button>
          <button
            onClick={() => setTab("media")}
            data-testid="tab-media"
            className={`px-4 py-2 text-xs font-mono uppercase tracking-widest border ${
              tab === "media" ? "bg-[#E4FF00] text-black border-[#E4FF00]" : "border-[#262626] text-[#A3A3A3]"
            }`}
          >
            <ImageIcon className="inline h-3 w-3 mr-1" /> Media
          </button>
        </div>

        {tab === "bookings" && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-[#A3A3A3] font-mono uppercase tracking-widest">Status</Label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                        data-testid="filter-status"
                        className="bg-[#0a0a0a] border border-[#262626] p-2 text-xs rounded-none">
                  <option value="all">All</option>
                  <option value="paid">Paid</option>
                  <option value="paid_manual">Paid · Manual</option>
                  <option value="partial_payment">Partial</option>
                  <option value="pending_payment">Pending</option>
                  <option value="created">Held (unpaid)</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-[#A3A3A3] font-mono uppercase tracking-widest">Date</Label>
                <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}
                       data-testid="filter-date"
                       className="bg-[#0a0a0a] border-[#262626] rounded-none w-40 text-xs" />
                {dateFilter && (
                  <button onClick={() => setDateFilter("")} className="text-[#A3A3A3] hover:text-[#f4f4f0]">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="text-xs text-[#666] font-mono ml-auto">{filteredBookings.length} shown</div>
            </div>

            {/* Table */}
            <div className="border border-[#262626] overflow-x-auto">
              <table className="w-full text-sm" data-testid="bookings-table">
                <thead className="bg-[#141414] text-[10px] font-mono uppercase tracking-widest text-[#A3A3A3]">
                  <tr>
                    <th className="text-left px-4 py-3">Date · Time</th>
                    <th className="text-left px-4 py-3">Customer</th>
                    <th className="text-left px-4 py-3">Package</th>
                    <th className="text-right px-4 py-3">Amount</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-[#666] font-mono text-xs">No bookings.</td>
                    </tr>
                  )}
                  {filteredBookings.map((b) => (
                    <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-t border-[#1a1a1a] hover:bg-[#0f0f0f]">
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs">{formatDateDMY(b.booking_date)}</div>
                        <div className="text-[#A3A3A3] text-xs">{formatTimeAmPm(b.start_time)} · {b.option_duration}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div>{b.name}</div>
                        <div className="text-[#A3A3A3] text-xs">{b.phone}{b.email ? " · " + b.email : ""}</div>
                      </td>
                      <td className="px-4 py-3 text-[#A3A3A3] text-xs">{b.package_name}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="font-display font-bold">₹{(b.amount || 0).toLocaleString("en-IN")}</div>
                        {b.status === "partial_payment" && (
                          <div className="text-[10px] font-mono text-orange-400">
                            paid ₹{(b.amount_paid || 0).toLocaleString("en-IN")} · pending ₹{(b.amount_pending || 0).toLocaleString("en-IN")}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${STATUS_META[b.status]?.cls || "bg-[#262626] text-[#A3A3A3]"} rounded-none text-[10px] font-mono uppercase`}>
                          {STATUS_META[b.status]?.label || b.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            onClick={() => openEdit(b)}
                            data-testid={`edit-booking-${b.id}`}
                            className="p-2 text-[#E4FF00] hover:bg-[#E4FF00]/10"
                            title="Edit booking"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => shareBookingWhatsApp(b)}
                            data-testid={`whatsapp-share-${b.id}`}
                            className="p-2 text-[#25D366] hover:bg-[#25D366]/10"
                            title="Share on WhatsApp"
                          >
                            <MessageCircle className="h-4 w-4" />
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm("Delete this booking? This frees up the slot.")) return;
                              await axios.delete(`${API}/admin/bookings/${b.id}`, { headers: authHeader() });
                              toast.success("Booking deleted");
                              refresh();
                            }}
                            data-testid={`delete-booking-${b.id}`}
                            className="p-2 text-red-400 hover:bg-red-500/10"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "calendar" && (
          <CalendarView
            month={calMonth}
            setMonth={setCalMonth}
            bookings={bookings}
            blocks={blocks}
            onDayClick={setDetailDate}
          />
        )}

        {tab === "share" && <ShareLinks packages={packages} />}

        {tab === "coupons" && <CouponsTab />}

        {tab === "briefs" && <BriefsTab onUnreadChange={setBriefsUnread} />}

        {tab === "packages" && <PackagesTab onSaved={loadPackages} />}

        {tab === "media" && <MediaTab />}
      </main>

      <ManualBookingDialog
        open={manualOpen}
        onOpenChange={(v) => { setManualOpen(v); if (!v) setEditBooking(null); }}
        packages={packages}
        initialDate={prefillDate}
        initialTime={prefillTime}
        initialBooking={editBooking}
        onCreated={refresh}
      />
      <BlockDialog
        open={blockOpen}
        onOpenChange={setBlockOpen}
        initialDate={prefillDate}
        onCreated={refresh}
      />
      <DayDetailDialog
        open={!!detailDate}
        onOpenChange={(v) => !v && setDetailDate(null)}
        date={detailDate}
        bookings={bookings}
        blocks={blocks}
        onRefresh={refresh}
        onManualBook={(d) => { openManual(d); setDetailDate(null); }}
        onBlock={(d) => { openBlock(d); setDetailDate(null); }}
      />
    </div>
  );
}

// -------- Calendar month view --------
function CalendarView({ month, setMonth, bookings, blocks, onDayClick }) {
  const days = useMemo(() => {
    const start = startOfMonth(month);
    const end = endOfMonth(month);
    const all = eachDayOfInterval({ start, end });
    // Pad start so month starts on Sunday
    const pad = start.getDay();
    const paddedStart = Array.from({ length: pad }, (_, i) => addDays(start, -pad + i));
    return [...paddedStart, ...all];
  }, [month]);

  const countFor = (d) => {
    const s = format(d, "yyyy-MM-dd");
    const b = bookings.filter((x) => x.booking_date === s).length;
    const bl = blocks.filter((x) => x.booking_date === s).length;
    return { b, bl };
  };

  return (
    <div className="border border-[#262626]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#262626]">
        <button onClick={() => setMonth(addDays(startOfMonth(month), -1))}
                data-testid="cal-prev-month"
                className="text-[#A3A3A3] hover:text-[#f4f4f0] p-2"><ChevronLeft className="h-4 w-4" /></button>
        <div className="font-display text-lg font-black">{format(month, "MMMM yyyy")}</div>
        <button onClick={() => setMonth(addDays(endOfMonth(month), 1))}
                data-testid="cal-next-month"
                className="text-[#A3A3A3] hover:text-[#f4f4f0] p-2"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 border-b border-[#262626] bg-[#141414]">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
          <div key={d} className="px-2 py-2 text-[10px] font-mono uppercase tracking-widest text-[#A3A3A3] text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const inMonth = d.getMonth() === month.getMonth();
          const { b, bl } = countFor(d);
          return (
            <button
              key={d.toISOString()}
              onClick={() => onDayClick(d)}
              data-testid={`cal-day-${format(d, "yyyy-MM-dd")}`}
              className={`aspect-square border-r border-b border-[#1a1a1a] p-2 flex flex-col justify-between text-left transition-colors ${
                inMonth ? "hover:bg-[#141414]" : "opacity-30"
              } ${isToday(d) && inMonth ? "bg-[#141414]" : ""}`}
            >
              <div className={`text-xs font-mono ${isToday(d) && inMonth ? "text-[#E4FF00]" : "text-[#A3A3A3]"}`}>
                {format(d, "d")}
              </div>
              {inMonth && (b + bl > 0) && (
                <div className="flex items-center gap-1 text-[10px] font-mono">
                  {b > 0 && <span className="bg-[#E4FF00] text-black px-1">{b}</span>}
                  {bl > 0 && <span className="bg-red-500 text-white px-1">B</span>}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// -------- Coupons tab --------
function CouponsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    code: "",
    discount_type: "flat",
    discount_value: "",
    expires_on: "",
    is_active: true,
  });
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/coupons`, { headers: authHeader() });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Failed to load coupons");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async (e) => {
    e.preventDefault();
    const code = form.code.trim().toUpperCase();
    const val = parseInt(form.discount_value, 10);
    if (!code) { toast.error("Enter a coupon code"); return; }
    if (!val || val <= 0) { toast.error("Discount must be a positive number"); return; }
    if (form.discount_type === "percent" && val > 100) { toast.error("Percentage must be 1-100"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/coupons`, {
        code,
        discount_type: form.discount_type,
        discount_value: val,
        expires_on: form.expires_on || "",
        is_active: form.is_active,
      }, { headers: authHeader() });
      toast.success(`Coupon ${code} created`);
      setForm({ code: "", discount_type: "flat", discount_value: "", expires_on: "", is_active: true });
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create coupon");
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await axios.patch(`${API}/admin/coupons/${c.id}`, { is_active: !c.is_active }, { headers: authHeader() });
      await refresh();
    } catch (e) { toast.error("Toggle failed"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/coupons/${c.id}`, { headers: authHeader() });
      toast.success("Coupon deleted");
      await refresh();
    } catch (e) { toast.error("Delete failed"); }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`${code} copied`);
    } catch (e) { toast.error("Copy failed"); }
  };

  return (
    <div className="space-y-6" data-testid="coupons-panel">
      {/* Create form */}
      <form onSubmit={submit} className="border border-[#262626] p-4 space-y-4">
        <div className="font-mono-overline text-[#E4FF00]">New coupon</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <Label className="font-mono-overline text-[#A3A3A3]">Code</Label>
            <Input
              data-testid="coupon-code-input"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="FRIEND20"
              className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none uppercase tracking-widest"
            />
          </div>
          <div>
            <Label className="font-mono-overline text-[#A3A3A3]">Type</Label>
            <select
              data-testid="coupon-type-select"
              value={form.discount_type}
              onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
              className="mt-2 w-full bg-[#0a0a0a] border border-[#262626] p-2 text-sm rounded-none focus:border-[#E4FF00] outline-none"
            >
              <option value="flat">Flat ₹</option>
              <option value="percent">Percent %</option>
            </select>
          </div>
          <div>
            <Label className="font-mono-overline text-[#A3A3A3]">
              Discount ({form.discount_type === "percent" ? "%" : "₹"})
            </Label>
            <Input
              data-testid="coupon-value-input"
              type="number"
              min="1"
              value={form.discount_value}
              onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
              placeholder={form.discount_type === "percent" ? "10" : "200"}
              className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none"
            />
          </div>
          <div>
            <Label className="font-mono-overline text-[#A3A3A3]">Expires on (optional)</Label>
            <Input
              data-testid="coupon-expiry-input"
              type="date"
              value={form.expires_on}
              onChange={(e) => setForm({ ...form, expires_on: e.target.value })}
              className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none"
            />
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              disabled={creating}
              data-testid="coupon-create-btn"
              className="w-full bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Create</>}
            </Button>
          </div>
        </div>
        <p className="text-xs text-[#A3A3A3] font-mono">
          Every coupon is single-use. Once redeemed on a successful payment it cannot be applied again — create a new code for each customer.
        </p>
      </form>

      {/* List */}
      <div className="border border-[#262626]">
        <div className="px-4 py-3 border-b border-[#262626] flex items-center justify-between">
          <div className="font-mono-overline text-[#A3A3A3]">Coupons · {items.length}</div>
          <Button variant="ghost" size="sm" onClick={refresh} className="text-[#A3A3A3] hover:text-[#f4f4f0] hover:bg-transparent">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-xs text-[#666] font-mono">
            {loading ? "Loading…" : "No coupons yet. Create one above."}
          </div>
        ) : (
          <div className="divide-y divide-[#1a1a1a]">
            {items.map((c) => {
              const isRedeemed = (c.used_count || 0) >= (c.max_uses || 1);
              return (
                <div key={c.id} data-testid={`coupon-row-${c.id}`}
                     className="p-4 flex flex-col md:flex-row md:items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-display font-black text-lg tracking-tight ${isRedeemed ? "line-through text-[#666]" : ""}`}>{c.code}</span>
                      <button onClick={() => copyCode(c.code)} className="text-[#A3A3A3] hover:text-[#E4FF00]" title="Copy code">
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                      {isRedeemed && (
                        <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-[#E4FF00] text-black">Redeemed</span>
                      )}
                      {!c.is_active && !isRedeemed && (
                        <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-[#262626] text-[#A3A3A3]">Inactive</span>
                      )}
                    </div>
                    <div className="text-xs text-[#A3A3A3] font-mono mt-1">
                      {c.discount_type === "percent" ? `${c.discount_value}% off` : `₹${c.discount_value} off`}
                      {" · Single-use"}
                      {c.expires_on ? ` · Expires ${c.expires_on}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {!isRedeemed && (
                      <Button
                        onClick={() => toggleActive(c)}
                        data-testid={`coupon-toggle-${c.id}`}
                        variant="ghost"
                        className={`border rounded-none text-xs h-8 px-3 ${c.is_active ? "border-[#E4FF00] text-[#E4FF00]" : "border-[#262626] text-[#A3A3A3]"}`}
                      >
                        {c.is_active ? <><Check className="h-3 w-3 mr-1" /> Active</> : "Enable"}
                      </Button>
                    )}
                    <Button
                      onClick={() => remove(c)}
                      data-testid={`coupon-delete-${c.id}`}
                      variant="ghost"
                      className="text-red-400 hover:text-red-300 hover:bg-transparent h-8 px-2"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// -------- Media library --------
function MediaTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState("");
  const fileRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const fullUrl = (item) => `${backend}${item.url}`;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/media`, { headers: authHeader() });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Failed to load media");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let ok = 0, fail = 0;
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        await axios.post(`${API}/admin/media/upload`, fd, {
          headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
        });
        ok++;
      } catch (err) {
        fail++;
        toast.error(err?.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
    if (ok > 0) toast.success(`Uploaded ${ok} file${ok > 1 ? "s" : ""}${fail ? ` (${fail} failed)` : ""}`);
    await refresh();
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.filename}? Any place using this URL will break.`)) return;
    try {
      await axios.delete(`${API}/admin/media/${m.id}`, { headers: authHeader() });
      toast.success("Deleted");
      await refresh();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const copyUrl = async (m) => {
    try {
      await navigator.clipboard.writeText(fullUrl(m));
      setCopiedId(m.id);
      toast.success("URL copied");
      setTimeout(() => setCopiedId(""), 1500);
    } catch (e) { toast.error("Copy failed"); }
  };

  return (
    <div className="space-y-4" data-testid="media-panel">
      <div className="border border-[#262626] p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-mono-overline text-[#E4FF00]">Media library</div>
          <p className="text-xs text-[#A3A3A3] mt-2 leading-relaxed max-w-2xl">
            Upload photos here once, then paste the URL wherever you need it (packages, gallery, hero). JPG / PNG / WEBP / GIF up to 15 MB. Files stay live forever unless you delete them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            multiple
            data-testid="media-file-input"
            onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
            className="hidden"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="media-upload-btn"
            className="bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
            Upload
          </Button>
          <Button variant="ghost" size="sm" onClick={refresh} className="text-[#A3A3A3] hover:text-[#f4f4f0] hover:bg-transparent" data-testid="media-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[#262626] p-10 text-center text-xs text-[#666] font-mono">
          {loading ? "Loading…" : uploading ? "Uploading…" : "No media yet. Click Upload to add photos."}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {items.map((m) => (
            <div key={m.id} data-testid={`media-row-${m.id}`} className="border border-[#262626] bg-[#0a0a0a] group">
              <div className="aspect-square bg-[#0f0f0f] flex items-center justify-center overflow-hidden">
                <img
                  src={fullUrl(m)}
                  alt={m.filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  data-testid={`media-img-${m.id}`}
                />
              </div>
              <div className="p-2">
                <div className="text-[11px] font-mono truncate text-[#f4f4f0]" title={m.filename}>{m.filename}</div>
                <div className="text-[10px] text-[#666] font-mono mt-0.5">
                  {(m.size / 1024).toFixed(1)} KB
                </div>
                <div className="flex items-center gap-1 mt-2">
                  <Button
                    onClick={() => copyUrl(m)}
                    data-testid={`media-copy-${m.id}`}
                    variant="ghost"
                    className="flex-1 border border-[#E4FF00] text-[#E4FF00] hover:bg-[#E4FF00]/10 rounded-none text-[10px] h-7 font-mono uppercase tracking-widest px-2"
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    {copiedId === m.id ? "Copied" : "URL"}
                  </Button>
                  <button
                    onClick={() => remove(m)}
                    data-testid={`media-delete-${m.id}`}
                    className="p-1.5 text-red-400 hover:bg-red-500/10"
                    title="Delete"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// -------- Packages tab --------
const CATEGORY_OPTIONS = ["photoshoot", "podcast", "video", "other"];

const emptyPackage = () => ({
  id: "",
  name: "",
  category: "photoshoot",
  description: "",
  includes: [],
  options: [{ duration: "Per Hour", price: 0 }],
  min_hours: 1,
  popular: false,
  active: true,
});

function PackagesTab({ onSaved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // full pkg object or null

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/packages`, { headers: authHeader() });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Failed to load packages");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (p) => {
    if (!window.confirm(`Delete package "${p.name}"? Existing bookings keep their record; only future bookings are blocked.`)) return;
    try {
      await axios.delete(`${API}/admin/packages/${p.id}`, { headers: authHeader() });
      toast.success("Package deleted");
      await refresh();
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="packages-panel">
      <div className="border border-[#262626] p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-mono-overline text-[#E4FF00]">Packages & pricing</div>
          <p className="text-xs text-[#A3A3A3] mt-2 leading-relaxed">
            Anything you edit here is instantly reflected on the pricing page, the booking dialog, and the pricing PDF. Existing bookings keep their captured price — updates apply to new bookings only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => setEditing(emptyPackage())} data-testid="pkg-new-btn"
                  className="bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold">
            <Plus className="h-4 w-4 mr-1" /> New package
          </Button>
          <Button variant="ghost" size="sm" onClick={refresh} className="text-[#A3A3A3] hover:text-[#f4f4f0] hover:bg-transparent" data-testid="pkg-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[#262626] p-10 text-center text-xs text-[#666] font-mono">
          {loading ? "Loading…" : "No packages yet."}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((p) => (
            <div key={p.id} data-testid={`pkg-row-${p.id}`}
                 className={`border p-4 ${p.active === false ? "opacity-60 border-[#1a1a1a]" : "border-[#262626]"}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-black text-lg tracking-tight">{p.name}</span>
                    <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-[#262626] text-[#A3A3A3]">{p.category || "other"}</span>
                    {p.popular && <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-[#E4FF00] text-black"><Star className="h-2.5 w-2.5 inline mr-1" />Popular</span>}
                    {p.active === false && <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-[#1a1a1a] text-[#A3A3A3]">Inactive</span>}
                  </div>
                  <div className="text-xs text-[#A3A3A3] font-mono mt-1">
                    id: <span className="text-[#f4f4f0]">{p.id}</span> · min {p.min_hours || 1} hr · {(p.options || []).length} price options · {(p.includes || []).length} inclusions
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                    {(p.options || []).map((o) => (
                      <div key={o.duration} className="text-xs font-mono">
                        <span className="text-[#A3A3A3]">{o.duration}: </span>
                        <span className="text-[#E4FF00] font-bold">₹{o.price.toLocaleString("en-IN")}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button onClick={() => setEditing(p)} variant="ghost"
                          data-testid={`pkg-edit-${p.id}`}
                          className="border border-[#E4FF00] text-[#E4FF00] hover:bg-[#E4FF00]/10 rounded-none text-xs h-8 px-3">
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  <Button onClick={() => remove(p)} variant="ghost"
                          data-testid={`pkg-delete-${p.id}`}
                          className="text-red-400 hover:text-red-300 hover:bg-transparent h-8 px-2">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PackageEditorDialog
        pkg={editing}
        onOpenChange={(v) => !v && setEditing(null)}
        onSaved={async () => { await refresh(); onSaved?.(); setEditing(null); }}
      />
    </div>
  );
}

function PackageEditorDialog({ pkg, onOpenChange, onSaved }) {
  const isCreate = pkg && !pkg.created_at;
  const [form, setForm] = useState(emptyPackage());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (pkg) {
      setForm({
        id: pkg.id || "",
        name: pkg.name || "",
        category: pkg.category || "photoshoot",
        description: pkg.description || "",
        includes: [...(pkg.includes || [])],
        options: (pkg.options && pkg.options.length ? pkg.options : [{ duration: "Per Hour", price: 0 }]).map((o) => ({ ...o })),
        min_hours: pkg.min_hours || 1,
        popular: !!pkg.popular,
        active: pkg.active !== false,
      });
    }
  }, [pkg]);

  if (!pkg) return null;

  const addOption = () => setForm((f) => ({ ...f, options: [...f.options, { duration: "", price: 0 }] }));
  const removeOption = (i) => setForm((f) => ({ ...f, options: f.options.filter((_, idx) => idx !== i) }));
  const setOption = (i, key, val) => setForm((f) => ({
    ...f,
    options: f.options.map((o, idx) => idx === i ? { ...o, [key]: key === "price" ? (Number(val) || 0) : val } : o),
  }));

  const setInclude = (i, val) => setForm((f) => ({ ...f, includes: f.includes.map((x, idx) => idx === i ? val : x) }));
  const addInclude = () => setForm((f) => ({ ...f, includes: [...f.includes, ""] }));
  const removeInclude = (i) => setForm((f) => ({ ...f, includes: f.includes.filter((_, idx) => idx !== i) }));

  const canSave = form.name.trim() && form.category.trim() && form.options.length > 0 &&
    form.options.every((o) => o.duration.trim() && Number(o.price) > 0);

  const submit = async () => {
    setLoading(true);
    try {
      const body = {
        name: form.name.trim(),
        category: form.category.trim().toLowerCase(),
        description: form.description,
        includes: form.includes.map((s) => s.trim()).filter(Boolean),
        options: form.options.map((o) => ({ duration: o.duration.trim(), price: Number(o.price) })),
        min_hours: Math.max(1, parseInt(form.min_hours, 10) || 1),
        popular: !!form.popular,
        active: !!form.active,
      };
      if (isCreate) {
        await axios.post(`${API}/admin/packages`, body, { headers: authHeader() });
        toast.success("Package created");
      } else {
        await axios.patch(`${API}/admin/packages/${form.id}`, body, { headers: authHeader() });
        toast.success("Package saved");
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!pkg} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[#0a0a0a] border border-[#262626] text-[#f4f4f0] rounded-none p-0 max-h-[92vh] overflow-y-auto">
        <DialogHeader className="border-b border-[#262626] px-6 py-5">
          <DialogTitle className="font-display text-2xl font-black tracking-tighter">
            {isCreate ? "New package" : `Edit · ${form.name}`}
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-mono-overline text-[#A3A3A3]">Name</Label>
              <Input data-testid="pkg-name"
                     value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="e.g. Studio Rental"
                     className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none" />
            </div>
            <div>
              <Label className="font-mono-overline text-[#A3A3A3]">Category</Label>
              <select data-testid="pkg-category"
                      value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                      className="mt-2 w-full bg-[#0a0a0a] border border-[#262626] p-2 text-sm rounded-none">
                {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label className="font-mono-overline text-[#A3A3A3]">Description</Label>
            <Textarea data-testid="pkg-description"
                      value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                      rows={2}
                      className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none" />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="font-mono-overline text-[#A3A3A3]">Duration options & pricing</Label>
              <Button type="button" onClick={addOption} data-testid="pkg-option-add"
                      variant="ghost"
                      className="text-[#E4FF00] hover:bg-transparent hover:text-[#CDE600] h-auto py-1 text-xs">
                <Plus className="h-3 w-3 mr-1" /> Add option
              </Button>
            </div>
            <div className="mt-2 space-y-2">
              {form.options.map((o, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`pkg-option-row-${i}`}>
                  <Input data-testid={`pkg-option-duration-${i}`}
                         value={o.duration}
                         onChange={(e) => setOption(i, "duration", e.target.value)}
                         placeholder="e.g. Per Hour · 4 Hours · Half Day"
                         className="flex-1 bg-[#0a0a0a] border-[#262626] rounded-none" />
                  <div className="relative w-40">
                    <IndianRupee className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#A3A3A3]" />
                    <Input data-testid={`pkg-option-price-${i}`}
                           type="number" min="1"
                           value={o.price}
                           onChange={(e) => setOption(i, "price", e.target.value)}
                           className="pl-7 bg-[#0a0a0a] border-[#262626] rounded-none" />
                  </div>
                  <button type="button" onClick={() => removeOption(i)}
                          data-testid={`pkg-option-remove-${i}`}
                          className="p-2 text-red-400 hover:bg-red-500/10">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[#666] font-mono mt-2">
              Include &quot;Per Hour&quot; if you want combo pricing (e.g. 5 hr = 4 Hours + 1 hr). Fixed options like &quot;4 Hours&quot; and &quot;8 Hours&quot; act as package anchors.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="font-mono-overline text-[#A3A3A3]">Includes (bullet list)</Label>
              <Button type="button" onClick={addInclude} data-testid="pkg-include-add"
                      variant="ghost"
                      className="text-[#E4FF00] hover:bg-transparent hover:text-[#CDE600] h-auto py-1 text-xs">
                <Plus className="h-3 w-3 mr-1" /> Add line
              </Button>
            </div>
            <div className="mt-2 space-y-2">
              {form.includes.length === 0 && (
                <p className="text-[10px] text-[#666] font-mono">No inclusions. Add lines like &quot;Studio space with props&quot;, &quot;Sony 4K Camera&quot;, etc.</p>
              )}
              {form.includes.map((it, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`pkg-include-row-${i}`}>
                  <Input data-testid={`pkg-include-${i}`}
                         value={it}
                         onChange={(e) => setInclude(i, e.target.value)}
                         placeholder="e.g. 2 x Godox VL200 Video Light"
                         className="flex-1 bg-[#0a0a0a] border-[#262626] rounded-none" />
                  <button type="button" onClick={() => removeInclude(i)}
                          data-testid={`pkg-include-remove-${i}`}
                          className="p-2 text-red-400 hover:bg-red-500/10">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="font-mono-overline text-[#A3A3A3]">Minimum hours</Label>
              <Input data-testid="pkg-min-hours"
                     type="number" min="1"
                     value={form.min_hours}
                     onChange={(e) => setForm({ ...form, min_hours: e.target.value })}
                     className="mt-2 bg-[#0a0a0a] border-[#262626] rounded-none" />
            </div>
            <label className="flex items-end gap-2 pb-2 cursor-pointer">
              <input type="checkbox"
                     data-testid="pkg-popular"
                     checked={form.popular}
                     onChange={(e) => setForm({ ...form, popular: e.target.checked })}
                     className="accent-[#E4FF00] mb-2" />
              <span className="text-xs font-mono uppercase tracking-widest text-[#A3A3A3] mb-2">
                Mark as popular (Most Booked badge)
              </span>
            </label>
            <label className="flex items-end gap-2 pb-2 cursor-pointer">
              <input type="checkbox"
                     data-testid="pkg-active"
                     checked={form.active}
                     onChange={(e) => setForm({ ...form, active: e.target.checked })}
                     className="accent-[#E4FF00] mb-2" />
              <span className="text-xs font-mono uppercase tracking-widest text-[#A3A3A3] mb-2">
                Active (visible on site)
              </span>
            </label>
          </div>
        </div>
        <div className="border-t border-[#262626] px-6 py-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="pkg-cancel"
                  className="text-[#A3A3A3] hover:text-[#f4f4f0] hover:bg-transparent">
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSave || loading} data-testid="pkg-save"
                  className="bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold px-6">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {isCreate ? "Create package" : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------- Briefs tab (contact form submissions) --------
function BriefsTab({ onUnreadChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/contacts?include_archived=${includeArchived}`,
        { headers: authHeader() }
      );
      setItems(data.items || []);
      onUnreadChange?.(data.unread_count || 0);
    } catch (e) {
      toast.error("Failed to load briefs");
    } finally {
      setLoading(false);
    }
  }, [includeArchived, onUnreadChange]);

  useEffect(() => { refresh(); }, [refresh]);

  const markRead = async (b, read) => {
    try {
      await axios.patch(`${API}/admin/contacts/${b.id}`, { read }, { headers: authHeader() });
      await refresh();
    } catch (e) { toast.error("Update failed"); }
  };

  const archive = async (b) => {
    try {
      await axios.patch(`${API}/admin/contacts/${b.id}`, { archived: !b.archived }, { headers: authHeader() });
      toast.success(b.archived ? "Restored" : "Archived");
      await refresh();
    } catch (e) { toast.error("Update failed"); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete brief from ${b.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/contacts/${b.id}`, { headers: authHeader() });
      toast.success("Deleted");
      await refresh();
    } catch (e) { toast.error("Delete failed"); }
  };

  const openWhatsApp = (b) => {
    if (!b.phone) { toast.error("No phone on this brief"); return; }
    const digits = String(b.phone).replace(/\D/g, "");
    const num = digits.length === 10 ? `91${digits}` : digits;
    const msg = `Hi ${b.name}, thanks for reaching out to LIME Studios about your shoot brief. When would you like to schedule?`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const copyEmail = async (e) => {
    try { await navigator.clipboard.writeText(e); toast.success("Email copied"); }
    catch (_) { toast.error("Copy failed"); }
  };

  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit", hour12: true });
    } catch { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="briefs-panel">
      <div className="border border-[#262626] p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-mono-overline text-[#E4FF00]">Got a brief?</div>
          <p className="text-xs text-[#A3A3A3] mt-2 leading-relaxed">
            Every submission from the site&apos;s contact form lands here. New briefs are marked <span className="text-[#E4FF00] font-bold">UNREAD</span> until you open them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#A3A3A3] cursor-pointer">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              data-testid="briefs-show-archived"
              className="accent-[#E4FF00]"
            />
            Show archived
          </label>
          <Button variant="ghost" size="sm" onClick={refresh} className="text-[#A3A3A3] hover:text-[#f4f4f0] hover:bg-transparent" data-testid="briefs-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[#262626] p-10 text-center text-xs text-[#666] font-mono">
          {loading ? "Loading…" : "No briefs yet. When a customer sends one from the site, it will appear here."}
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((b) => {
            const isOpen = expanded === b.id;
            const isUnread = !b.read && !b.archived;
            return (
              <div
                key={b.id}
                data-testid={`brief-row-${b.id}`}
                className={`border p-4 transition-colors ${
                  isUnread ? "border-[#E4FF00]/40 bg-[#141400]/40" : b.archived ? "border-[#1a1a1a] opacity-60" : "border-[#262626]"
                }`}
              >
                <div
                  className="flex flex-wrap items-start justify-between gap-3 cursor-pointer"
                  onClick={() => {
                    setExpanded(isOpen ? null : b.id);
                    if (!isOpen && !b.read) markRead(b, true);
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-black text-base tracking-tight">{b.name}</span>
                      {isUnread && (
                        <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-[#E4FF00] text-black">Unread</span>
                      )}
                      {b.archived && (
                        <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest bg-[#262626] text-[#A3A3A3]">Archived</span>
                      )}
                    </div>
                    <div className="text-xs text-[#A3A3A3] font-mono mt-1 truncate">
                      {b.phone && <span>{b.phone}</span>}
                      {b.phone && b.email && <span> · </span>}
                      {b.email && <span>{b.email}</span>}
                    </div>
                    {!isOpen && (
                      <div className="text-sm text-[#f4f4f0]/80 mt-2 line-clamp-2">
                        {b.message}
                      </div>
                    )}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#666] shrink-0">
                    {fmtTime(b.created_at)}
                  </div>
                </div>

                {isOpen && (
                  <div className="mt-4 pt-4 border-t border-[#262626] space-y-4">
                    <div className="text-sm text-[#f4f4f0] whitespace-pre-wrap leading-relaxed" data-testid={`brief-message-${b.id}`}>
                      {b.message}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {b.phone && (
                        <Button
                          onClick={() => openWhatsApp(b)}
                          data-testid={`brief-wa-${b.id}`}
                          className="bg-[#25D366] hover:bg-[#1EBE5A] text-black rounded-none font-bold text-xs h-8"
                        >
                          <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
                        </Button>
                      )}
                      {b.email && (
                        <>
                          <a
                            href={`mailto:${b.email}?subject=${encodeURIComponent("Your shoot brief — LIME Studios")}&body=${encodeURIComponent(`Hi ${b.name},\n\nThanks for reaching out about your shoot brief.\n\n`)}`}
                            data-testid={`brief-email-${b.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-mono uppercase tracking-widest border border-[#262626] hover:border-[#E4FF00] text-[#f4f4f0]"
                          >
                            <Mail className="h-3.5 w-3.5" /> Reply
                          </a>
                          <button
                            onClick={() => copyEmail(b.email)}
                            className="text-[#A3A3A3] hover:text-[#E4FF00] p-1.5"
                            title="Copy email"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                      <div className="flex-1" />
                      <Button
                        onClick={() => markRead(b, !b.read)}
                        variant="ghost"
                        data-testid={`brief-toggle-read-${b.id}`}
                        className="text-[#A3A3A3] hover:text-[#f4f4f0] hover:bg-transparent text-xs h-8"
                      >
                        {b.read ? "Mark unread" : "Mark read"}
                      </Button>
                      <Button
                        onClick={() => archive(b)}
                        variant="ghost"
                        data-testid={`brief-archive-${b.id}`}
                        className="text-[#A3A3A3] hover:text-[#f4f4f0] hover:bg-transparent text-xs h-8"
                      >
                        <Archive className="h-3.5 w-3.5 mr-1" /> {b.archived ? "Restore" : "Archive"}
                      </Button>
                      <Button
                        onClick={() => remove(b)}
                        variant="ghost"
                        data-testid={`brief-delete-${b.id}`}
                        className="text-red-400 hover:text-red-300 hover:bg-transparent text-xs h-8"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// -------- Share booking links panel --------
function ShareLinks({ packages }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const [copiedId, setCopiedId] = useState("");

  const copy = async (url, id) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      toast.success("Link copied");
      setTimeout(() => setCopiedId(""), 1500);
    } catch (e) {
      toast.error("Copy failed — long-press to select the link");
    }
  };

  const waShareUrl = (pkg, url) => {
    const msg = `Book *${pkg.name}* at LIME Studios directly here:\n${url}\n\nSound-treated studio in Baner, Pune. See you soon!`;
    return `https://wa.me/?text=${encodeURIComponent(msg)}`;
  };

  return (
    <div className="space-y-4" data-testid="share-links-panel">
      <div className="border border-[#262626] p-4">
        <div className="font-mono-overline text-[#E4FF00]">Direct booking links</div>
        <p className="text-xs text-[#A3A3A3] mt-2 leading-relaxed">
          Share these links with customers. Opening one auto-launches the booking flow with the right package pre-selected — one less click for them.
        </p>
      </div>

      <div className="space-y-2">
        {packages.length === 0 && (
          <div className="text-xs text-[#666] font-mono py-6 text-center border border-dashed border-[#262626]">
            Loading packages…
          </div>
        )}
        {packages.map((p) => {
          const url = `${origin}/book/${p.id}`;
          return (
            <div key={p.id} data-testid={`share-row-${p.id}`}
                 className="border border-[#262626] p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:border-[#333]">
              <div className="min-w-0 flex-1">
                <div className="font-display font-black tracking-tight truncate">{p.name}</div>
                <div className="font-mono text-[11px] text-[#A3A3A3] break-all mt-1">{url}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  onClick={() => copy(url, p.id)}
                  data-testid={`share-copy-${p.id}`}
                  className="bg-[#E4FF00] text-black hover:bg-[#CDE600] rounded-none font-bold text-xs"
                >
                  <Copy className="h-3.5 w-3.5 mr-1" />
                  {copiedId === p.id ? "Copied" : "Copy"}
                </Button>
                <a
                  href={waShareUrl(p, url)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`share-wa-${p.id}`}
                  className="inline-flex items-center gap-1 px-3 py-2 text-xs font-mono uppercase tracking-widest border border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10"
                >
                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                </a>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`share-open-${p.id}`}
                  className="inline-flex items-center gap-1 px-3 py-2 text-xs font-mono uppercase tracking-widest border border-[#262626] text-[#A3A3A3] hover:text-[#f4f4f0]"
                >
                  Open
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// -------- Router root --------
export default function AdminApp() {
  return (
    <Routes>
      <Route path="/login" element={<AdminLogin />} />
      <Route path="/" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
    </Routes>
  );
}
